<template>
  <div class="merchant-price-page">
    <basic-container>
      <avue-crud
        ref="crud"
        :option="tableOption"
        :data="tableData"
        :page="page"
        :table-loading="tableLoading"
        @on-load="handleList"
        @search-change="handleSearch"
        @search-reset="handleReset"
        @selection-change="handleSelectionChange"
        @refresh-change="handleList"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      >
        <template slot="menuLeft">
          <el-button type="primary" icon="el-icon-plus" size="small" @click="handleAdd">
            新建价目表
          </el-button>
          <el-button
            type="danger"
            icon="el-icon-delete"
            size="small"
            plain
            :disabled="!selectedRows.length"
            @click="handleBatchDelete"
          >
            批量删除
          </el-button>
          <el-button
            type="warning"
            icon="el-icon-tickets"
            size="small"
            plain
            :disabled="!pendingChangeCount"
            @click="openPendingDialog"
          >
            待提交审批{{ pendingChangeCount ? `（${pendingChangeCount}）` : '' }}
          </el-button>
        </template>

        <template slot="threshold" slot-scope="scope">
          <span v-if="scope.row.threshold != null">{{ scope.row.threshold }}</span>
          <span v-else style="color: #c0c4cc">-</span>
        </template>
        <template slot="effectiveStartTime" slot-scope="scope">
          <span v-if="scope.row.effectiveStartTime">{{ displayTime(scope.row.effectiveStartTime) }}</span>
          <span v-else style="color: #c0c4cc">立即生效</span>
        </template>
        <template slot="effectiveEndTime" slot-scope="scope">
          <span v-if="scope.row.effectiveEndTime">{{ displayTime(scope.row.effectiveEndTime) }}</span>
          <span v-else style="color: #c0c4cc">长期有效</span>
        </template>
        <template slot="effectiveStatus" slot-scope="scope">
          <el-tag :type="scope.row.effectiveStatusType" size="small">
            {{ scope.row.effectiveStatus }}
          </el-tag>
        </template>
        <template slot="activeAdjustTaskCount" slot-scope="scope">
          <el-tag v-if="Number(scope.row.activeAdjustTaskCount) > 0" type="danger" size="small">
            {{ scope.row.activeAdjustTaskCount }} 条生效
          </el-tag>
          <span v-else style="color: #c0c4cc">无</span>
        </template>
        <template slot="status" slot-scope="scope">
          <el-tag :type="priceBookStatusType(scope.row.status)" size="small">
            {{ priceBookStatusText(scope.row.status) }}
          </el-tag>
        </template>
        <template slot="approvalStatus" slot-scope="scope">
          <el-tag
            v-if="scope.row.pendingAction || scope.row.pendingApproval"
            :type="approvalStatusType(scope.row)"
            size="small"
          >
            {{ approvalStatusText(scope.row) }}
          </el-tag>
          <span v-else style="color: #c0c4cc">-</span>
        </template>
        <template slot="createTime" slot-scope="scope">
          <span v-if="scope.row.createTime">{{ displayTime(scope.row.createTime) }}</span>
          <span v-else style="color: #c0c4cc">-</span>
        </template>

        <template slot-scope="scope" slot="menu">
          <el-button type="text" icon="el-icon-setting" size="small" @click="handleMerchantPrice(scope.row)">
            商户价
          </el-button>
          <el-button type="text" icon="el-icon-tickets" size="small" @click="handleAdjustTask(scope.row)">
            调价任务
          </el-button>
          <el-button type="text" icon="el-icon-edit" size="small" :disabled="scope.row.pendingApproval" @click="handleEdit(scope.row)">
            编辑
          </el-button>
          <el-button
            v-if="scope.row.priceBookType && scope.row.priceBookType !== 'BASE'"
            type="text"
            icon="el-icon-copy-document"
            size="small"
            @click="handleCopy(scope.row)"
          >
            复制
          </el-button>
          <el-button
            type="text"
            :icon="toggleActionIcon(scope.row)"
            size="small"
            :disabled="scope.row.pendingApproval || scope.row.pendingAction === 'BOOK_DELETE'"
            @click="handleToggleStatus(scope.row)"
          >
            {{ toggleActionText(scope.row) }}
          </el-button>
          <el-button
            type="text"
            icon="el-icon-delete"
            size="small"
            style="color: #f56c6c"
            :disabled="scope.row.pendingApproval || scope.row.pendingAction === 'BOOK_DELETE'"
            @click="handleDelete(scope.row)"
          >
            删除
          </el-button>
        </template>
      </avue-crud>
    </basic-container>

    <!-- 价盘主表操作暂存：启停、生效、删除一次性提交审批 -->
    <el-dialog
      title="待提交的价盘操作"
      :visible.sync="pendingDialogVisible"
      width="760px"
      append-to-body
      :close-on-click-modal="false"
    >
      <el-alert
        title="以下操作尚未提交审批，提交后统一生成 1 张审批单，审批通过后统一生效。"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 12px"
      />
      <el-table
        ref="pendingTable"
        :data="pendingDialogRows"
        row-key="_pendingIndex"
        border
        stripe
        size="small"
        max-height="360"
        @selection-change="handlePendingSelectionChange"
      >
        <el-table-column type="selection" width="45" align="center" />
        <el-table-column type="index" label="序号" width="60" align="center" />
        <el-table-column prop="name" label="价目表名称" min-width="180" show-overflow-tooltip />
        <el-table-column prop="code" label="价盘编号" min-width="130" show-overflow-tooltip />
        <el-table-column label="操作" width="110" align="center">
          <template slot-scope="scope">
            {{ pendingActionText(scope.row.action, scope.row.status, scope.row.oldStatus) }}
          </template>
        </el-table-column>
        <el-table-column label="当前状态" width="100" align="center">
          <template slot-scope="scope">{{ priceBookStatusText(scope.row.oldStatus) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="80" align="center" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" size="small" icon="el-icon-delete" @click="removePendingChange(scope.row._pendingIndex)">移除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer" class="dialog-footer">
        <el-button @click="pendingDialogVisible = false">关闭</el-button>
        <el-button
          type="danger"
          plain
          :disabled="!pendingSelectedRows.length"
          @click="removeSelectedPendingChanges"
        >批量移除{{ pendingSelectedRows.length ? `（${pendingSelectedRows.length}）` : '' }}</el-button>
        <el-button
          type="primary"
          :disabled="!pendingChangeCount"
          :loading="pendingSubmitting"
          @click="submitPendingChanges"
        >提交审核{{ pendingChangeCount ? `（${pendingChangeCount} 条操作）` : '' }}</el-button>
      </span>
    </el-dialog>

    <!-- 价目表新增/编辑弹窗 -->
    <PriceBookFormDialog
      :visible.sync="formDialogVisible"
      :mode="formDialogMode"
      :price-book="currentRow"
      @submit-success="handleList"
    />

    <!-- 商户价配置弹窗 -->
    <MerchantPriceDrawer
      :visible.sync="merchantDialogVisible"
      :price-book="currentRow"
    />
  </div>
</template>

<script>
import { priceBookTableOption } from "@/const/work/waterProof/merchantPriceOption";
import {
  findPriceBookPage,
  copyPriceBook,
  submitPriceBookActionBatch,
} from "@/api/work/waterProof/merchantPrice";
import { displayTime } from "@/util/util";
import PriceBookFormDialog from "./components/PriceBookFormDialog.vue";
import MerchantPriceDrawer from "./components/MerchantPriceDrawer.vue";

export default {
  name: "merchantPrice",
  components: {
    PriceBookFormDialog,
    MerchantPriceDrawer,
  },
  data() {
    return {
      tableOption: priceBookTableOption,
      tableData: [],
      tableLoading: false,
      selectedRows: [],
      page: { total: 0, currentPage: 1, pageSize: 10 },
      searchParams: {},
      formDialogVisible: false,
      formDialogMode: "add",
      merchantDialogVisible: false,
      currentRow: null,
      // 价盘主表操作暂存队列，按浏览器保存，提交前不写后端
      pendingChanges: [],
      pendingDialogVisible: false,
      pendingSubmitting: false,
      pendingSelectedRows: [],
    };
  },
  computed: {
    pendingChangeCount() {
      return this.pendingChanges.length;
    },
    pendingDialogRows() {
      return this.pendingChanges.map((row, index) => Object.assign({ _pendingIndex: index }, row));
    },
  },
  created() {
    this.loadPendingChanges();
  },
  methods: {
    parseData(res) {
      if (!res || res.data == null) return {};
      if (typeof res.data === "string") {
        try {
          return JSON.parse(res.data) || {};
        } catch (e) {
          console.error("parseData error:", e);
          return {};
        }
      }
      return res.data;
    },
    // 时间转时间戳：兼容时间戳与 "yyyy-MM-dd HH:mm:ss" 字符串（字符串 - 换 / 兼容 Safari 解析）
    toTime(v) {
      if (v == null || v === "") return null;
      if (typeof v === "number") return v;
      const s = String(v);
      if (/^\d+$/.test(s)) return Number(s);
      const t = new Date(s.replace(/-/g, "/")).getTime();
      return isNaN(t) ? null : t;
    },
    // 表格展示：后端返回展示格式字符串时直接显示，时间戳/ISO 串走格式化
    displayTime,
    priceBookStatusText(status) {
      return { DRAFT: "草稿", ENABLE: "启用", DISABLE: "停用" }[status] || status || "-";
    },
    priceBookStatusType(status) {
      return { DRAFT: "warning", ENABLE: "success", DISABLE: "info" }[status] || "info";
    },
    pendingStorageKey() {
      return "joins-sales-cloud:price-book-actions-pending";
    },
    loadPendingChanges() {
      try {
        const value = window.localStorage.getItem(this.pendingStorageKey());
        const parsed = value ? JSON.parse(value) : [];
        this.pendingChanges = Array.isArray(parsed) ? parsed : [];
      } catch (e) {
        console.warn("loadPendingChanges error:", e);
        this.pendingChanges = [];
      }
    },
    persistPendingChanges() {
      try {
        if (this.pendingChanges.length) {
          window.localStorage.setItem(this.pendingStorageKey(), JSON.stringify(this.pendingChanges));
        } else {
          window.localStorage.removeItem(this.pendingStorageKey());
        }
      } catch (e) {
        console.warn("persistPendingChanges error:", e);
      }
    },
    pendingTargetStatus(row) {
      const pending = row && row.pendingAction === "BOOK_STATUS"
        ? row
        : this.pendingChanges.find((item) => item.priceBookId === (row && row.id));
      return pending && pending.action === "BOOK_STATUS" ? pending.status : (row && row.status);
    },
    toggleActionText(row) {
      const current = row && row.status;
      const target = this.pendingTargetStatus(row);
      const pending = row && row.pendingAction === "BOOK_STATUS"
        ? row
        : this.pendingChanges.find((item) => item.priceBookId === (row && row.id));
      if (current === "DRAFT") {
        return pending && pending.action === "BOOK_STATUS" && pending.status === "ENABLE"
          ? "取消生效"
          : "生效";
      }
      // 操作按钮展示的是点击后要执行的动作，而不是当前状态。
      return target === "ENABLE" ? "停用" : "启用";
    },
    toggleActionIcon(row) {
      return this.toggleActionText(row) === "停用" ? "el-icon-close" : "el-icon-check";
    },
    pendingActionText(action, targetStatus, oldStatus) {
      if (action === "BOOK_DELETE") return "删除";
      if (action === "BOOK_STATUS") {
        if (targetStatus === "ENABLE" && oldStatus === "DRAFT") return "生效";
        return targetStatus === "ENABLE" ? "启用" : "停用";
      }
      return action || "-";
    },
    approvalStatusText(row) {
      if (row && row.pendingAction) {
        return `待提交审批：${this.pendingActionText(
          row.pendingAction,
          row.pendingTargetStatus,
          row.status
        )}`;
      }
      if (row && row.pendingApproval) {
        return row.pendingApprovalAction ? `审批中：${row.pendingApprovalAction}` : "审批中";
      }
      return "-";
    },
    approvalStatusType(row) {
      return row && row.pendingAction ? "warning" : "primary";
    },
    // 生效状态：草稿 / 已停用 / 未生效 / 生效中 / 已失效
    computeEffectiveStatus(row) {
      if (row.status === "DRAFT") return { text: "草稿", type: "warning" };
      if (row.status === "DISABLE") return { text: "已停用", type: "info" };
      const now = Date.now();
      const start = this.toTime(row.effectiveStartTime);
      const end = this.toTime(row.effectiveEndTime);
      if (start && now < start) {
        return { text: "未生效", type: "warning" };
      }
      if (end && now > end) {
        return { text: "已失效", type: "danger" };
      }
      return { text: "生效中", type: "success" };
    },
    buildParams() {
      // 接口 page 从 1 开始
      const params = {
        page: this.page.currentPage,
        size: this.page.pageSize,
      };
      Object.keys(this.searchParams).forEach((k) => {
        const v = this.searchParams[k];
        if (v !== "" && v !== null && v !== undefined) params[k] = v;
      });
      return params;
    },
    handleList() {
      this.tableLoading = true;
      findPriceBookPage(this.buildParams())
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            const rows = this.applyPendingChanges(data.rows || []);
            rows.forEach((r) => {
              const s = this.computeEffectiveStatus(r);
              this.$set(r, "effectiveStatus", s.text);
              this.$set(r, "effectiveStatusType", s.type);
              // 预先写入字段，兼容 Avue 对未出现在后端响应中的自定义列不渲染插槽。
              this.$set(r, "approvalStatus", this.approvalStatusText(r));
            });
            this.tableData = rows;
            this.page.total = data.total || 0;
          } else {
            this.tableData = [];
            this.page.total = 0;
            this.$message.error(res.message || "加载失败");
          }
        })
        .catch((err) => {
          console.error("handleList error:", err);
          this.tableData = [];
          this.page.total = 0;
          this.$message.error("加载异常");
        })
        .finally(() => {
          this.tableLoading = false;
        });
    },
    handleSearch(params) {
      this.searchParams = params || {};
      this.page.currentPage = 1;
      this.handleList();
    },
    handleReset() {
      this.searchParams = {};
      this.page.currentPage = 1;
      this.handleList();
    },
    handlePageChange(page) {
      this.page.currentPage = page;
      this.handleList();
    },
    handleSizeChange(size) {
      this.page.pageSize = size;
      this.page.currentPage = 1;
      this.handleList();
    },
    handleSelectionChange(selection) {
      this.selectedRows = selection || [];
    },
    handleAdd() {
      this.formDialogMode = "add";
      this.currentRow = null;
      this.formDialogVisible = true;
    },
    handleEdit(row) {
      if (row && row.pendingApproval) {
        this.$message.warning("该价盘已有审批中的操作，请先处理审批单");
        return;
      }
      if (row && row.pendingAction === "BOOK_DELETE") {
        this.$message.warning("该价盘已暂存删除，请先移除删除操作");
        return;
      }
      this.formDialogMode = "edit";
      this.currentRow = row;
      this.formDialogVisible = true;
    },
    handleCopy(row) {
      this.$confirm(
        `确定复制价目表「${row.name}」吗？复制后会包含计价项标准价、型号标准价和全部商户价，并保存为草稿。`,
        "复制价目表",
        {
          confirmButtonText: "确定复制",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(() => copyPriceBook(row.id))
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res);
            this.$message.success(`复制成功：${data.name || "新价目表"}（${data.code || ""}），请核对后点击“生效”`);
            this.handleList();
          } else {
            this.$message.error(res.message || "复制失败");
          }
        })
        .catch((err) => {
          // 用户取消确认时不提示错误。
          if (err) console.error("handleCopy error:", err);
        });
    },
    handleMerchantPrice(row) {
      this.currentRow = row;
      this.merchantDialogVisible = true;
    },
    // 跳转调价管理页，并默认选中当前价盘
    handleAdjustTask(row) {
      this.$router.push({
        path: "/work/waterProof/merchantPrice/adjustTask",
        query: { priceBookId: row && row.id ? row.id : undefined },
      });
    },
    // 草稿生效 / 停用 / 启用：只写入暂存队列，最后统一提交审批
    handleToggleStatus(row) {
      if (!row || row.pendingApproval || row.pendingAction === "BOOK_DELETE") {
        if (row && row.pendingApproval) this.$message.warning("该价盘已有审批中的操作，请先处理审批单");
        return;
      }
      const pending = this.pendingChanges.find((item) => item.priceBookId === row.id);
      const currentTarget = this.pendingTargetStatus(row);
      // 草稿暂存“生效”后再次点击表示撤销暂存，不能生成“停用草稿”操作。
      const undoDraftEnable = row.status === "DRAFT"
        && pending && pending.action === "BOOK_STATUS" && pending.status === "ENABLE";
      const target = undoDraftEnable ? row.status : (currentTarget === "ENABLE" ? "DISABLE" : "ENABLE");
      const actionText = undoDraftEnable ? "取消生效" : (row.status === "DRAFT" && target === "ENABLE"
        ? "生效" : (target === "ENABLE" ? "启用" : "停用"));
      this.$confirm(`确定${actionText}价目表「${row.name}」吗？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          if (undoDraftEnable || target === row.status) {
            this.removePendingChangeByBookId(row.id);
            this.$message.success("已取消暂存操作");
          } else {
            this.queuePendingChange({
              action: "BOOK_STATUS",
              priceBookId: row.id,
              name: row.name,
              code: row.code,
              oldStatus: row.status,
              status: target,
            });
            this.$message.success(`${actionText}操作已暂存，请统一提交审批`);
          }
          this.handleList();
        })
        .catch(() => {});
    },
    handleDelete(row) {
      if (!row || row.pendingApproval || row.pendingAction === "BOOK_DELETE") {
        if (row && row.pendingApproval) this.$message.warning("该价盘已有审批中的操作，请先处理审批单");
        return;
      }
      // 删除审批通过后才级联删除，要求输入价目表名称二次确认
      this.$prompt(
        `提交审批后将级联删除该价盘下全部商户价，且不可恢复！请输入价目表名称「${row.name}」以暂存删除。`,
        "警告",
        {
          confirmButtonText: "确定删除",
          cancelButtonText: "取消",
          type: "warning",
          customClass: "price-book-delete-prompt",
          inputValidator: (val) => (val || "").trim() === row.name || "输入的价目表名称不一致",
        }
      )
        .then(() => {
          this.queuePendingChange({
            action: "BOOK_DELETE",
            priceBookId: row.id,
            name: row.name,
            code: row.code,
            oldStatus: row.status,
          });
          this.$message.success("删除操作已暂存，请统一提交审批");
          this.handleList();
        })
        .catch(() => {});
    },
    handleBatchDelete() {
      const rows = this.selectedRows.filter((row) => !row.pendingApproval && row.pendingAction !== "BOOK_DELETE");
      if (!rows.length) {
        if (this.selectedRows.length) this.$message.warning("选中的价盘已有审批或已暂存删除，无法重复操作");
        else this.$message.warning("请先选择要删除的项");
        return;
      }
      this.$prompt(
        `将暂存删除选中的 ${rows.length} 张价目表，审批通过后才级联删除其下全部商户价。请输入 DELETE 以确认。`,
        "警告",
        {
          confirmButtonText: "确定删除",
          cancelButtonText: "取消",
          type: "warning",
          customClass: "price-book-delete-prompt",
          inputPattern: /^DELETE$/,
          inputErrorMessage: "请输入 DELETE 以确认",
        }
      )
        .then(() => {
          rows.forEach((row) => {
            this.queuePendingChange({
              action: "BOOK_DELETE",
              priceBookId: row.id,
              name: row.name,
              code: row.code,
              oldStatus: row.status,
            });
          });
          this.selectedRows = [];
          this.$message.success("删除操作已暂存，请统一提交审批");
          this.handleList();
        })
        .catch(() => {});
    },
    queuePendingChange(change) {
      const index = this.pendingChanges.findIndex((item) => item.priceBookId === change.priceBookId);
      if (index < 0) this.pendingChanges.push(change);
      else this.$set(this.pendingChanges, index, change);
      this.persistPendingChanges();
    },
    removePendingChangeByBookId(priceBookId) {
      const index = this.pendingChanges.findIndex((item) => item.priceBookId === priceBookId);
      if (index >= 0) {
        this.pendingChanges.splice(index, 1);
        this.persistPendingChanges();
      }
    },
    applyPendingChanges(rows) {
      const pendingMap = {};
      this.pendingChanges.forEach((item) => { pendingMap[item.priceBookId] = item; });
      return rows.map((row) => {
        const pending = pendingMap[row.id];
        if (pending) {
          this.$set(row, "pendingAction", pending.action);
          this.$set(row, "pendingTargetStatus", pending.status);
        } else {
          this.$delete(row, "pendingAction");
          this.$delete(row, "pendingTargetStatus");
        }
        return row;
      });
    },
    openPendingDialog() {
      if (!this.pendingChangeCount) {
        this.$message.info("暂无待提交的价盘操作");
        return;
      }
      this.pendingSelectedRows = [];
      this.pendingDialogVisible = true;
    },
    handlePendingSelectionChange(rows) {
      this.pendingSelectedRows = rows || [];
    },
    removePendingChange(index) {
      if (!Number.isInteger(Number(index))) return;
      this.pendingChanges.splice(Number(index), 1);
      this.pendingSelectedRows = [];
      this.persistPendingChanges();
      if (!this.pendingChanges.length) this.pendingDialogVisible = false;
      this.handleList();
    },
    removeSelectedPendingChanges() {
      const indexes = this.pendingSelectedRows
        .map((row) => Number(row && row._pendingIndex))
        .filter((index) => Number.isInteger(index) && index >= 0 && index < this.pendingChanges.length)
        .sort((a, b) => b - a);
      if (!indexes.length) return;
      this.$confirm(`确定移除选中的 ${indexes.length} 条暂存操作吗？`, "提示", {
        confirmButtonText: "确定移除",
        cancelButtonText: "取消",
        type: "warning",
      }).then(() => {
        indexes.forEach((index) => this.pendingChanges.splice(index, 1));
        this.pendingSelectedRows = [];
        this.persistPendingChanges();
        if (!this.pendingChanges.length) this.pendingDialogVisible = false;
        this.handleList();
      }).catch(() => {});
    },
    submitPendingChanges() {
      if (!this.pendingChanges.length || this.pendingSubmitting) return;
      this.pendingSubmitting = true;
      submitPriceBookActionBatch({ items: this.pendingChanges })
        .then((res) => {
          if (res.code === "200") {
            const data = this.parseData(res) || {};
            this.pendingChanges = [];
            this.pendingSelectedRows = [];
            this.persistPendingChanges();
            this.pendingDialogVisible = false;
            this.$message.success(data.message || "已提交审批，审批通过后生效");
            this.handleList();
          } else {
            this.$message.error(res.message || "提交审批失败");
          }
        })
        .catch((err) => {
          console.error("submitPendingChanges error:", err);
          this.$message.error("提交审批异常");
        })
        .finally(() => {
          this.pendingSubmitting = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.merchant-price-page {
  ::v-deep .el-tabs__header {
    margin-bottom: 8px;
  }
}
</style>

<style lang="scss">
/* Element UI 2.x 按提示文字整体居中警告图标，多行提示时会压到输入框。 */
.price-book-delete-prompt .el-message-box__status {
  top: 16px;
  transform: none;
}
</style>
